import { useAuth0 } from '@auth0/auth0-react';
import { createBrowserHistory } from 'history';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import {
  useColorScheme,
  useTheme,
} from '@mui/material/styles';

import { TenantService } from '@pingum/api-services';

import { userHasFullAppRole } from '../../utils/roleAccessHelper';
import { getTenantFromUrl } from '../../utils/urlHelper';

import LoadingPlaceholder from '../../components/LoadingPlaceholder';
import AppBar from './appBar';

import GlobalModals from '../../components/GlobalModals';
import AccessDeniedCard from '../../components/UserStatusAndDisplayCards/AccessDeniedCard';
import ErrorLoggingInCard from '../../components/UserStatusAndDisplayCards/ErrorLoggingInCard';
import LimitedAccessCard from '../../components/UserStatusAndDisplayCards/LimitedAccessCard';
import LimitedPaidClientAccessCard from '../../components/UserStatusAndDisplayCards/LimitedPaidClientAccessCard';
import NoTenantCard from '../../components/UserStatusAndDisplayCards/NoTenantCard';
import OrganizationDeactivatedCard from '../../components/UserStatusAndDisplayCards/OrganizationDeactivatedCard';
import PleaseLogInCard from '../../components/UserStatusAndDisplayCards/PleaseLogInCard';
import questriTheme from '../../utils/theme';

export const history = createBrowserHistory();

const isUserInTenant = (userTenants, tenant) => userTenants.indexOf(tenant) > -1;
// TODO fix this for domain specific
const tenantUrl = TenantService.getTenantUrl(getTenantFromUrl()).replace('pingum', 'questri').replace('questri.io', 'questri.app');

// TODO move this to a styles.js and classes format
const defaultContainerStyle = {
  // backgroundColor: 'rgb(244, 244, 244)',
  width: '100vw',
  minHeight: '100vh',
  overflowX: 'hidden',
};
const getContentToRender = (isLoading, loadingTimeout, auth0Error, isAuthenticated, accessDenied, deactivatedTenant, children) => {
  if (isLoading) {
    return <LoadingPlaceholder />;
  }
  if ((window || { location: { pathname: '' } }).location.pathname.indexOf('logout') >= 0) {
    return children; // return logout page if requested
  }
  if ((window || { location: { pathname: '' } }).location.pathname.indexOf('schedules') >= 0) {
    return children; // return schedule page
  }
  if ((window || { location: { pathname: '' } }).location.pathname.indexOf('invoices') >= 0) {
    return children; // return schedule page
  }
  if ((window || { location: { pathname: '' } }).location.pathname.indexOf('public/horses') >= 0) {
    return children; // return public horse page
  }
  if (auth0Error) {
    const queryString = window.location.search;
    const query = {};
    const pairs = (queryString[0] === '?'
      ? queryString.substr(1)
      : queryString).split('&');
    for (let i = 0; i < pairs.length; i += 1) {
      const pair = pairs[i].split('=');
      query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
    }
    return (
      <ErrorLoggingInCard query={query} />
    );
  }
  if (!isAuthenticated) {
    return (
      <PleaseLogInCard />
    );
  }
  if (accessDenied && !loadingTimeout) {
    return (
      <LoadingPlaceholder />
    );
  }
  if (accessDenied && loadingTimeout) {
    return (
      <AccessDeniedCard tenantUrl={tenantUrl} />
    );
  }
  if (deactivatedTenant) {
    return (
      <OrganizationDeactivatedCard tenantUrl={tenantUrl} />
    );
  }

  // Success! All checks pass so show content.
  return children;
};

const AppContainerComponent = ({
  children,
  isLoading,
  navigate,
  chatClient,
  unreadCount,
}) => {
  const theme = useTheme();
  const { mode } = useColorScheme();
  const fullscreen = useMediaQuery(questriTheme.breakpoints.down('md'));

  const tenants = useSelector((state) => state.user.tenants);
  const isClient = useSelector((state) => state.user.isClient);
  const paidClientAccess = useSelector((state) => state.user.paidClientAccess);
  const permissions = useSelector((state) => state.user.permissions);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const tenantObject = useSelector((state) => state.tenant.tenant);
  const inlineDrawer = useSelector((state) => state.app.inlineDrawer);
  const checkEmailUser = useSelector((state) => state.user.checkEmailUser);

  const [loadingTimeout, setLoadingTimeout] = React.useState(false);

  React.useEffect(() => {
    // it takes a little more time for access to load so put a 5 second timeout in place to account for that
    if (!loadingTimeout) {
      setTimeout(() => {
        setLoadingTimeout(true)
      }, [5000])
    }
  }, [])

  // Auth0 values
  const {
    user,
    isAuthenticated,
    error: auth0Error,
  } = useAuth0();

  // Tenant information
  const tenant = getTenantFromUrl();
  const accessDenied = isAuthenticated && tenant && !isUserInTenant(tenants.map((t) => t.tenant), tenant) && !!checkEmailUser;
  const deactivatedTenant = isAuthenticated && tenant && (tenantObject
    ? tenantObject.deactivated
    : false);
  const pathname = (window && window.location && window.location.pathname) || '';
  const clientBoardPath = pathname.startsWith('/board')
    || pathname.startsWith('/tasks/planning')
    || pathname.startsWith('/tasks/calendar');
  const canAccessFullApp = permissions.owner || permissions.admin || permissions.staff;
  const checkEmailUserHasFullAppRole = userHasFullAppRole(checkEmailUser && checkEmailUser.roles);
  const canShowClientAccessCard = isClient && !canAccessFullApp && !checkEmailUserHasFullAppRole;
  const canShowTenantClientAccess = !accessDenied && !deactivatedTenant && !!tenant && tenant !== 'questri'
    && canShowClientAccessCard && !clientBoardPath && isAuthenticated && !isLoading;
  const showLimitedAccessCard = canShowTenantClientAccess && !paidClientAccess;
  const showLimitedPaidClientAccessCard = canShowTenantClientAccess && paidClientAccess;

  /*
    Methods
  */
  const paletteForMode = theme.colorSchemes?.[mode]?.palette || theme.palette;
  const background = paletteForMode.background?.default || theme.palette.background?.default;
  const textColor = paletteForMode.text?.primary || theme.palette.text?.primary;

  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.style.backgroundColor = background;
      document.body.style.backgroundColor = background;
      document.body.style.color = textColor;
    }
  }, [background, textColor]);

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        navigate={navigate}
        isLoading={isLoading}
        tenantObject={tenantObject}
        tenant={tenant}
        chatClient={chatClient}
        unreadCount={unreadCount}
      />
      <Box
        component="main"
        style={defaultContainerStyle}
        sx={{
          flexGrow: 1,
          p: {
            xs: '56px 0 3rem',
            sm: '48px 0 3rem',
          },
          display: 'flex',
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: (!fullscreen && !!inlineDrawer)
              ? `calc(100% - ${450}px) !important`
              : '100%',
          }}
        >

          {getContentToRender(isLoading, loadingTimeout, auth0Error, isAuthenticated, accessDenied, deactivatedTenant, children)}

          {/* Authenticated user not on a tenant URL */}
          {(!accessDenied && !deactivatedTenant && (!tenant || tenant === 'questri') && isAuthenticated && !isLoading) && (
            <NoTenantCard barns={tenants} authUser={user} />
          )}
          {/* User is non staff role and has no horse access to a horse with PAID CLIENT Experience enabled */}
          {showLimitedAccessCard && (
            <LimitedAccessCard selectedBarn={selectedBarn || {}} checkEmailUser={checkEmailUser} />
          )}
          {/* User is non staff role and but has PAID CLIENT Experience enabled */}
          {showLimitedPaidClientAccessCard && (
            <LimitedPaidClientAccessCard selectedBarn={selectedBarn || {}} checkEmailUser={checkEmailUser} />
          )}
        </Box>
        <GlobalModals statusChange task horse addEditSchedule />
      </Box>
    </Box>
  );
};

AppContainerComponent.propTypes = {
  children: PropTypes.any.isRequired,
  isLoading: PropTypes.bool.isRequired,
  navigate: PropTypes.func.isRequired,
  chatClient: PropTypes.any,
  unreadCount: PropTypes.number,
};

AppContainerComponent.defaultProps = {
  chatClient: null,
  unreadCount: 0,
};

export default AppContainerComponent;
